import { useEffect, useState } from "react";
import { rpc } from "../../services/datos";
import { botonPrimario, botonSecundario, campo, etiqueta, fechaLarga, hora } from "../../shared/estilos";
import Esqueleto from "../../shared/components/Esqueleto";

const CLAVE_RECUERDO = "mi_casa_recuerdo";

const leerRecuerdo = () => {
  try {
    return localStorage.getItem(CLAVE_RECUERDO);
  } catch {
    return null;
  }
};

const guardarRecuerdo = (valor) => {
  try {
    if (valor) localStorage.setItem(CLAVE_RECUERDO, valor);
    else localStorage.removeItem(CLAVE_RECUERDO);
  } catch {
    // Sin almacenamiento la persona escribirá su correo cada vez.
  }
};

// Página pública que abre el QR de la entrada: la persona registra su asistencia
// (y la de su familia) desde su propio celular. La ruta le entrega la reunión y el código.
function RegistroAsistencia({ servicioId, codigo }) {
  const [reunion, setReunion] = useState(null);
  const [recuerdo, setRecuerdo] = useState(leerRecuerdo);
  const [correo, setCorreo] = useState("");
  const [registro, setRegistro] = useState(null);
  const [elegidos, setElegidos] = useState(new Set());
  const [mensajeFamilia, setMensajeFamilia] = useState("");
  const [error, setError] = useState("");
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    rpc("registro_reunion", { p_servicio: Number(servicioId), p_codigo: codigo })
      .then(setReunion).catch((e) => setError(e.message));
  }, [servicioId, codigo]);

  const registrar = async (datos) => {
    setEnviando(true);
    setError("");
    try {
      const respuesta = await rpc("registro_registrar", {
        p_servicio: Number(servicioId), p_codigo: codigo, p_email: datos.email || null, p_recuerdo: datos.recuerdo || null,
      });
      guardarRecuerdo(respuesta.recuerdo);
      setRecuerdo(respuesta.recuerdo);
      setRegistro(respuesta);
      setElegidos(new Set(respuesta.familia.filter((p) => !p.presente).map((p) => `${p.tipo}-${p.id}`)));
    } catch (e) {
      if (datos.recuerdo) {
        guardarRecuerdo(null);
        setRecuerdo(null);
      }
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const registrarFamilia = async () => {
    const ids = (tipo) => [...elegidos].filter((c) => c.startsWith(`${tipo}-`)).map((c) => Number(c.split("-")[1]));
    setEnviando(true);
    setError("");
    try {
      const respuesta = await rpc("registro_familia", {
        p_servicio: Number(servicioId), p_codigo: codigo, p_recuerdo: recuerdo, p_miembro_ids: ids("miembro"), p_nino_ids: ids("nino"),
      });
      setRegistro({ ...registro, familia: respuesta.familia });
      setElegidos(new Set());
      setMensajeFamilia(`Registramos a ${respuesta.registrados} persona(s) de tu familia.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setEnviando(false);
    }
  };

  const olvidar = () => {
    guardarRecuerdo(null);
    setRecuerdo(null);
    setRegistro(null);
  };

  const alternar = (clave) => {
    const nuevo = new Set(elegidos);
    nuevo.has(clave) ? nuevo.delete(clave) : nuevo.add(clave);
    setElegidos(nuevo);
  };

  const pendientesFamilia = registro?.familia.filter((p) => !p.presente) || [];

  return (
    <div className="flex min-h-screen flex-col items-center bg-black px-5 py-10 text-white">
      <header className="mb-10 text-center">
        <p className="text-4xl font-black uppercase tracking-tighter">Mi Casa</p>
        <div className="mx-auto my-3 h-px w-16 bg-white/60" />
        <p className="text-xs font-light uppercase tracking-[0.35em] text-white/80">La familia que buscabas</p>
      </header>

      <main className="w-full max-w-sm">
        {reunion && (
          <div className="mb-6 text-center">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-gray-500">Registro de asistencia</p>
            <h1 className="mt-2 text-2xl font-black">{reunion.nombre}</h1>
            <p className="mt-1 text-sm capitalize text-gray-400">{fechaLarga(reunion.fecha)}{reunion.hora ? ` · ${hora(reunion.hora)}` : ""}</p>
          </div>
        )}

        {error && <p role="alert" className="rounded-xl border border-rose-500/30 bg-rose-500/10 text-rose-200 mb-5 p-4 text-sm">{error}</p>}

        {!reunion && !error && <Esqueleto tipo="texto" />}

        {reunion && !reunion.abierto && (
          <p className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6 text-center text-sm text-gray-300">
            El registro de esta reunión se abre el mismo día de la reunión. ¡Te esperamos!
          </p>
        )}

        {reunion?.abierto && !registro && (
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6">
            {recuerdo ? (
              <>
                <p className="text-center text-sm text-gray-300">Toca el botón para registrar que llegaste.</p>
                <button onClick={() => registrar({ recuerdo })} disabled={enviando} className={`${botonPrimario} mt-5 w-full py-5 text-base`}>
                  {enviando ? "Registrando..." : "¡Estoy aquí!"}
                </button>
                <button onClick={olvidar} className="mt-4 w-full text-center text-xs text-gray-500 underline">No soy yo / usar otro correo</button>
              </>
            ) : (
              <>
                <label htmlFor="correo" className={etiqueta}>Tu correo</label>
                <input id="correo" type="email" inputMode="email" autoComplete="email" autoCapitalize="none" value={correo}
                  onChange={(e) => setCorreo(e.target.value)} onKeyDown={(e) => e.key === "Enter" && registrar({ email: correo })}
                  className={`${campo} text-lg`} placeholder="Ej: nombre@correo.com" />
                <p className="mt-2 text-xs text-gray-500">Solo la primera vez: este celular te recordará.</p>
                <button onClick={() => registrar({ email: correo })} disabled={enviando || !correo.includes("@")} className={`${botonPrimario} mt-5 w-full py-4`}>
                  {enviando ? "Registrando..." : "Registrar mi asistencia"}
                </button>
              </>
            )}
          </div>
        )}

        {registro && (
          <div className="rounded-2xl border border-white/10 bg-zinc-900/60 p-6">
            <p className="text-center text-3xl font-black">¡Bienvenido{registro.persona.nombres ? `, ${registro.persona.nombres}` : ""}!</p>
            <p className="mt-2 text-center text-sm text-gray-300">Tu asistencia quedó registrada. Qué bueno tenerte en casa.</p>

            {registro.familia.length > 0 && (
              <div className="mt-6 border-t border-gray-800 pt-5">
                <p className={etiqueta}>¿Vino alguien de tu familia contigo?</p>
                <ul className="space-y-2">
                  {registro.familia.map((p) => {
                    const clave = `${p.tipo}-${p.id}`;
                    return (
                      <li key={clave}>
                        <label className={`flex items-center gap-3 rounded-xl border p-3 ${p.presente ? "border-white/10 text-gray-500" : "border-gray-700"}`}>
                          <input type="checkbox" className="h-5 w-5" disabled={p.presente}
                            checked={p.presente || elegidos.has(clave)} onChange={() => alternar(clave)} />
                          <span>{p.nombres}{p.tipo === "nino" ? " (Kids)" : ""}{p.presente ? " · ya registrado" : ""}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
                {pendientesFamilia.length > 0 && (
                  <button onClick={registrarFamilia} disabled={enviando || elegidos.size === 0} className={`${botonSecundario} mt-4 w-full`}>
                    {enviando ? "Registrando..." : "Registrar a mi familia"}
                  </button>
                )}
                {mensajeFamilia && <p className="mt-3 text-center text-sm text-gray-300">{mensajeFamilia}</p>}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

export default RegistroAsistencia;
